import { useState, useEffect } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/**
 * Banner per installazione PWA
 * Appare solo su dispositivi compatibili quando l'app non è già installata
 */
export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    // Verifica se è già stato dismissato di recente
    const dismissed = localStorage.getItem('pwa-prompt-dismissed')
    if (dismissed) {
      const dismissedDate = new Date(dismissed)
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)
      // Mostra di nuovo dopo 7 giorni
      if (daysSinceDismissed < 7) return
    }

    // Rileva iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent)
    const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator as any).standalone
    setIsIOS(isIOSDevice && !isInStandaloneMode)

    // Su iOS mostra il prompt con istruzioni manuali
    if (isIOSDevice && !isInStandaloneMode) {
      // Mostra dopo 3 secondi per non essere invasivo
      const timer = setTimeout(() => setShowPrompt(true), 3000)
      return () => clearTimeout(timer)
    }

    // Per altri browser, cattura l'evento beforeinstallprompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      // Mostra dopo 3 secondi
      setTimeout(() => setShowPrompt(true), 3000)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)

    // Nascondi se già installata
    window.addEventListener('appinstalled', () => {
      setShowPrompt(false)
      setDeferredPrompt(null)
    })

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
    }
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      setShowPrompt(false)
    }
    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString())
  }

  if (!showPrompt) return null

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-night-light border border-gold/30 rounded-xl shadow-2xl p-4 z-50 animate-fade-in">
      <div className="flex items-start gap-4">
        {/* Icona */}
        <div className="flex-shrink-0 w-12 h-12 bg-gold/20 rounded-xl flex items-center justify-center">
          <img
            src="/images/logoscontornato.png"
            alt="Conero Garage"
            className="w-8 h-8"
          />
        </div>

        {/* Contenuto */}
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-medium mb-1">
            Installa l'app
          </h3>

          {isIOS ? (
            <p className="text-gray-400 text-sm">
              Tocca <span className="inline-flex items-center mx-1 text-gold">
                {/* Icona Share di Safari iOS */}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15M12 15V3m0 0l3 3m-3-3l-3 3" />
                </svg>
              </span> in basso e poi <strong className="text-white">"Aggiungi a Home"</strong>
            </p>
          ) : (
            <p className="text-gray-400 text-sm">
              Accedi ai locali anche offline, direttamente dalla home del telefono.
            </p>
          )}

          {/* Pulsanti */}
          <div className="flex items-center gap-3 mt-3">
            {!isIOS && (
              <button
                onClick={handleInstall}
                className="px-4 py-2 bg-gold text-night text-sm font-medium rounded-lg hover:bg-gold-light transition-colors"
              >
                Installa
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="px-4 py-2 text-gray-400 text-sm hover:text-white transition-colors"
            >
              {isIOS ? 'Ho capito' : 'Non ora'}
            </button>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 text-gray-500 hover:text-white transition-colors"
          aria-label="Chiudi"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  )
}
